import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingPlaceholder } from '@/common/components/LoadingPlaceholder/LoadingPlaceholder';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useGetCardsData } from '@/common/integrations/menhir/employee/card/card.hooks';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { IAddress } from '@/common/types/general';
import { useCardConfigurationMarket } from '@/common/utils/card';
import { CardShippingAddressStep } from './Steps/CardShippingAddress/CardShippingAddressStep';
import { ChoseNameOnCardStep } from './Steps/ChoseNameOnCard/ChoseNameOnCardStep';
import { ConfirmDetailsStep } from './Steps/ConfirmDetails/ConfirmDetailsStep';

export const CardRequest = () => {
  const { t } = useTranslation();

  const cardsData = useGetCardsData();
  const { company } = useEmployeeDetails();
  const cardConfiguration = useCardConfigurationMarket(company.market.slug);

  //Form Fields
  const [address, setAddress] = useState<IAddress>();
  const [cardHolderName, setCardHolderName] = useState<string | undefined>(
    (cardsData?.digitalCard?.holderName ||
      cardsData?.expiredCard?.holderName ||
      cardsData?.expiringCard?.holderName) ??
      ''
  );

  if (cardsData.isInitialLoading) {
    return <LoadingPlaceholder />;
  }

  const hasAddressStep = company.settings.cardShippingStrategy === 'employee';

  return (
    <FlowLayout.Root
      headerProps={{
        title: t('dashboard.card.actions.new_request.flow.title'),
      }}
    >
      <FlowLayout.Step
        title={t('dashboard.card.actions.new_request.flow.step1.title')}
      >
        <ChoseNameOnCardStep
          cardHolderName={cardHolderName}
          setCardHolderName={setCardHolderName}
        />
      </FlowLayout.Step>
      {hasAddressStep && (
        <FlowLayout.Step
          title={t('dashboard.card.actions.new_request.flow.step2.title')}
        >
          <CardShippingAddressStep
            initialAddress={address}
            setAddress={setAddress}
            onBack={(submittedAddress) => {
              setAddress(submittedAddress);
            }}
            config={cardConfiguration}
            suggestedCountry={company.market.slug.toUpperCase()}
            digitalCardId={cardsData?.digitalCard?.id}
          />
        </FlowLayout.Step>
      )}
      <FlowLayout.Step
        title={t('dashboard.card.actions.new_request.flow.step3.title')}
      >
        <ConfirmDetailsStep
          cardHolderName={cardHolderName!}
          address={address}
          digitalCardId={cardsData?.digitalCard?.id}
        />
      </FlowLayout.Step>
    </FlowLayout.Root>
  );
};
